import { useMemo } from 'react';
import styled from 'styled-components';

import { useEnvironment } from 'context/environment';
import { useProfile } from 'context/profile';
import { ImportType } from 'models/Client';
import { PERMISSIONS } from 'models/Profile';
import AllPatientsIcon from 'svg/AllPatientsIcon';
import CoordinatePatientsIcon from 'svg/CoordinatePatientsIcon';
import GearIcon from 'svg/GearIcon';
import InsightsIcon from 'svg/InsightsIcon';
import PatientPortfolioIcon from 'svg/PatientPortfolioIcon';
import ReferralsIcon from 'svg/ReferralIcon';

import SideNavLink from './SideNavLink';

const SideNavbar = () => {
  const { environment } = useEnvironment();
  const { profile } = useProfile();

  const showConnectPatients = useMemo(() => {
    return (
      profile.has(PERMISSIONS.patientCreate) &&
      profile.actingClient.importType === ImportType.IMPORTED_PATIENT &&
      profile.canActAsManager &&
      (profile.isAdmin || (!profile.isPhysicianTeamUser && !profile.isAffiliateUser))
    );
  }, [profile]);

  const showReferrals = !!profile.actingClient.providedProviderTypes.length;
  const hasHealthPlans = profile.actingClient.providedProviderTypes.some((x) => x.config.designatesAcceptedPlans);

  return (
    <NavContainer>
      <NavContent>
        <SideNavLink to='/patients' title='Patient Portfolio' icon={<PatientPortfolioIcon width={18} height={18} />} />
        <SideNavLink to='/all-patients' title='All Patients' icon={<AllPatientsIcon width={18} height={18} />} />
        {showReferrals && (
          <SideNavLink
            data-cy='referrals-link'
            to={
              hasHealthPlans
                ? [
                    {
                      subtitle: 'Active Referrals',
                      to: '/referrals',
                    },
                    {
                      subtitle: 'Contracted Health Plans',
                      to: '/contracted-health-plans',
                    },
                  ]
                : '/referrals'
            }
            title='Referrals'
            icon={<ReferralsIcon width={18} height={18} />}
          />
        )}
        <SideNavLink
          to='/coordinate'
          data-cy='coordinateLink'
          title='Coordinate'
          icon={<CoordinatePatientsIcon color={'currentColor'} width={18} height={18} />}
          visible={showConnectPatients}
        />
        <SideNavLink to='/insights' title='Olio Insights' icon={<InsightsIcon width={18} height={18} />} />
        <SideNavLink
          to='/'
          domain={environment?.ADMIN_UI_DOMAIN}
          title='Admin'
          icon={<GearIcon width={18} height={18} />}
          visible={profile.isAdmin}
        />
      </NavContent>
    </NavContainer>
  );
};

export default SideNavbar;

const NavContainer = styled.div`
  height: 100%;
  min-width: ${({ theme }) => theme.dimensions.sideNavWidth};
  background-color: ${({ theme }) => theme.colors.black};
  flex: 0;
  display: none;

  @media ${({ theme }) => theme.devices.desktop} {
    display: flex;
  }
`;

const NavContent = styled.nav`
  display: flex;
  flex: 1;
  flex-direction: column;
`;
